import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, Checkbox, Form, Input, message, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { BankOutlined, EditOutlined, KeyOutlined, LockOutlined, PlusOutlined, SafetyCertificateOutlined, SearchOutlined, UnlockOutlined } from "@ant-design/icons";
import { assignableRoles, hasPermission } from "../auth/permissions";
import { createUser, getUserPermissions, listUsers, resetUserPassword, updateUser, updateUserPermissions } from "../services/userApi";
import { getUserAssignments, listAllActiveBankAccounts, replaceUserAssignments } from "../services/bankAccountApi";
import ManagementPanel from "../components/management/ManagementPanel";

const ROLE_LABELS = {
  SUPER_ADMIN: "Quản trị cao nhất",
  ADMIN: "Quản trị viên",
  OPERATOR: "Nhân viên nhập liệu",
  AUDITOR: "Kiểm toán viên",
};

function ActionButton({ title, icon, onClick, danger }) {
  return <Tooltip title={title}><Button className="user-action-button" aria-label={title} icon={icon} onClick={onClick} danger={danger} /></Tooltip>;
}

export default function SystemUsers({ currentUser }) {
  const [pageData, setPageData] = useState({ content: [], page: 0, size: 20, totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const assignmentPendingRef = useRef(false);
  const [assignmentUser, setAssignmentUser] = useState(null);
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const [assignmentIds, setAssignmentIds] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [permissionUser, setPermissionUser] = useState(null);
  const [permissionRows, setPermissionRows] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [permissionsCustomized, setPermissionsCustomized] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState();
  const [statusFilter, setStatusFilter] = useState();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const canEdit = hasPermission(currentUser, "users:edit");
  const filterMounted = useRef(false);
  const loadSequence = useRef(0);

  async function load(page = 0, size = pageData.size || 20) {
    const request = ++loadSequence.current;
    setLoading(true);
    try {
      const filters = {};
      if (search.trim()) filters.query = search.trim();
      if (roleFilter) filters.role = roleFilter;
      if (statusFilter !== undefined) filters.active = statusFilter;
      const result = await listUsers({ page, size, ...filters });
      if (request === loadSequence.current) setPageData(result);
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách người dùng");
    } finally {
      if (request === loadSequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    load(0, 20);
    // The initial request is intentionally stable; later requests are driven by table pagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filterMounted.current) { filterMounted.current = true; return; }
    const timer = setTimeout(() => load(0, pageData.size), 250);
    return () => { clearTimeout(timer); loadSequence.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, statusFilter]);

  function openCreate() {
    if (savingRef.current) return;
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: true });
    setModalOpen(true);
  }

  function openEdit(record) {
    if (savingRef.current) return;
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  }

  async function save(values) {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const payload = editing ? { ...values, password: null } : values;
    try {
      if (editing) await updateUser(editing.id, payload);
      else await createUser(payload);
      message.success(editing ? "Đã cập nhật người dùng" : "Đã tạo người dùng");
      setModalOpen(false);
      await load(pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể lưu người dùng");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  function closeModal() {
    if (!savingRef.current) setModalOpen(false);
  }

  async function openAssignments(user) {
    if (assignmentPendingRef.current) return;
    assignmentPendingRef.current = true;
    setAssignmentUser(user);
    setAssignmentLoading(true);
    try {
      const [accounts, assigned] = await Promise.all([
        listAllActiveBankAccounts(),
        getUserAssignments(user.id),
      ]);
      setAssignmentOptions(accounts);
      setAssignmentIds((assigned || []).map((account) => account.id));
    } catch (error) {
      message.error(error.message || "Không thể tải phân công tài khoản");
      setAssignmentUser(null);
    } finally {
      assignmentPendingRef.current = false;
      setAssignmentLoading(false);
    }
  }

  async function saveAssignments() {
    if (!assignmentUser || assignmentPendingRef.current) return;
    assignmentPendingRef.current = true;
    setAssignmentSaving(true);
    try {
      await replaceUserAssignments(assignmentUser.id, assignmentIds);
      message.success("Đã cập nhật phân công tài khoản");
      setAssignmentUser(null);
      setAssignmentOptions([]);
      setAssignmentIds([]);
      await load(pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể lưu phân công tài khoản");
    } finally {
      assignmentPendingRef.current = false;
      setAssignmentSaving(false);
    }
  }

  function closeAssignments() {
    if (assignmentPendingRef.current) return;
    setAssignmentUser(null);
    setAssignmentOptions([]);
    setAssignmentIds([]);
  }

  async function openPermissions(user) {
    setPermissionUser(user);
    setPermissionLoading(true);
    try {
      const result = await getUserPermissions(user.id);
      setPermissionRows(result.permissions || []);
      setPermissionsCustomized(Boolean(result.customized));
    } catch (error) {
      message.error(error.message || "Không thể tải phân quyền thao tác");
      setPermissionUser(null);
    } finally {
      setPermissionLoading(false);
    }
  }

  function updatePermission(feature, field, checked) {
    setPermissionRows((rows) => rows.map((row) => {
      if (row.feature !== feature) return row;
      if (field === "canView" && !checked) return { ...row, canView: false, canEdit: false, canDelete: false };
      if (field !== "canView" && checked) return { ...row, [field]: true, canView: true };
      return { ...row, [field]: checked };
    }));
  }

  function togglePermissionColumn(field, checked) {
    setPermissionRows((rows) => rows.map((row) => {
      const supported = field === "canView" || (field === "canEdit" ? row.supportsEdit : row.supportsDelete);
      if (!supported) return row;
      if (field === "canView" && !checked) return { ...row, canView: false, canEdit: false, canDelete: false };
      if (field !== "canView" && checked) return { ...row, [field]: true, canView: true };
      return { ...row, [field]: checked };
    }));
  }

  async function savePermissions() {
    if (!permissionUser || permissionSaving || permissionLoading) return;
    setPermissionSaving(true);
    try {
      const payload = permissionRows.map(({ feature, canView, canEdit, canDelete }) => ({ feature, canView, canEdit, canDelete }));
      await updateUserPermissions(permissionUser.id, payload);
      message.success("Đã lưu phân quyền thao tác");
      setPermissionUser(null);
    } catch (error) {
      message.error(error.message || "Không thể lưu phân quyền thao tác");
    } finally {
      setPermissionSaving(false);
    }
  }

  async function savePassword(values) {
    if (!passwordUser || passwordSaving) return;
    setPasswordSaving(true);
    try {
      await resetUserPassword(passwordUser.id, values);
      message.success("Đã đặt lại mật khẩu người dùng");
      setPasswordUser(null);
      passwordForm.resetFields();
    } catch (error) {
      message.error(error.message || "Không thể đặt lại mật khẩu");
    } finally {
      setPasswordSaving(false);
    }
  }

  async function toggleStatus(record) {
    try {
      await updateUser(record.id, {
        username: record.username, displayName: record.displayName, email: record.email,
        role: record.role, status: !record.status, password: null, phoneNumber: record.phoneNumber,
      });
      message.success(record.status ? "Đã khóa người dùng" : "Đã kích hoạt người dùng");
      await load(pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể đổi trạng thái người dùng");
    }
  }

  const roles = assignableRoles(currentUser.role, canEdit);
  const canManageUser = (record) => canEdit && record.id !== currentUser.id
    && record.role !== "SUPER_ADMIN"
    && (currentUser.role === "SUPER_ADMIN" || ["OPERATOR", "AUDITOR"].includes(record.role));
  const filteredUsers = pageData.content.filter((user) => {
    const keyword = search.trim().toLowerCase();
    return (!keyword || [user.username, user.displayName, user.email, user.phoneNumber].some((value) => String(value || "").toLowerCase().includes(keyword)))
      && (!roleFilter || user.role === roleFilter)
      && (statusFilter === undefined || user.status === statusFilter);
  });
  const columns = [
    { title: "Người dùng", key: "identity", width: 235, render: (_, user) => <div className="table-person"><Avatar shape="square">{(user.displayName || user.username).split(" ").slice(-2).map(s => s[0]).join("")}</Avatar><div className="table-identity"><strong>{user.displayName}</strong><small>@{user.username}</small></div></div> },
    { title: "Liên hệ", key: "contact", width: 215, render: (_, user) => <div className="table-identity"><strong>{user.phoneNumber || "Chưa cập nhật"}</strong><small>{user.email}</small></div> },
    { title: "Vai trò", dataIndex: "role", render: (role) => <Tag color="blue">{ROLE_LABELS[role] || role}</Tag> },
    {
      title: "Tài khoản được phân",
      dataIndex: "assignedAccountCodes",
      width: 190,
      render: (codes = []) => codes.length
        ? <Space size={[4, 4]} wrap>{codes.map((code) => <Tag color="blue" key={code}>{code}</Tag>)}</Space>
        : <Typography.Text type="secondary">Chưa phân công</Typography.Text>,
    },
    { title: "Trạng thái", dataIndex: "status", render: (active) => <Tag color={active ? "green" : "default"}>{active ? "Hoạt động" : "Đã khóa"}</Tag> },
    {
      title: "Thao tác",
      width: 210,
      fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          {canManageUser(record) && <ActionButton title="Chỉnh sửa" icon={<EditOutlined />} onClick={() => openEdit(record)} />}
          {canManageUser(record) && record.status && ["OPERATOR", "AUDITOR"].includes(record.role) &&
            <ActionButton title="Phân công tài khoản" icon={<BankOutlined />} onClick={() => openAssignments(record)} />}
          {canManageUser(record) && <ActionButton title="Phân quyền thao tác" icon={<SafetyCertificateOutlined />} onClick={() => openPermissions(record)} />}
          {canManageUser(record) && <ActionButton title="Đặt lại mật khẩu" icon={<KeyOutlined />} onClick={() => { passwordForm.resetFields(); setPasswordUser(record); }} />}
          {canManageUser(record) && <Popconfirm title={record.status ? "Khóa người dùng này?" : "Kích hoạt lại người dùng này?"}
            okText={record.status ? "Khóa" : "Kích hoạt"} cancelText="Hủy" onConfirm={() => toggleStatus(record)}>
            <span><ActionButton title={record.status ? "Khóa người dùng" : "Kích hoạt người dùng"}
              icon={record.status ? <LockOutlined /> : <UnlockOutlined />} danger={record.status} /></span>
          </Popconfirm>}
        </Space>
      ),
    },
  ];

  return (
    <div className="upgrade-page">
      <div className="page-heading">
        <div><Typography.Title level={2}>Quản lý người dùng</Typography.Title>
          <Typography.Text type="secondary">Quản lý nhân sự, thông tin liên hệ và quyền truy cập.</Typography.Text></div>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={saving}>Thêm người dùng</Button>}
      </div>
      <ManagementPanel title="Danh sách người dùng" total={pageData.totalElements} filters={<div className="user-filter-bar">
        <Input allowClear prefix={<SearchOutlined />} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Tìm tên, tên đăng nhập, số điện thoại…" />
        <Select allowClear placeholder="Tất cả vai trò" value={roleFilter} onChange={setRoleFilter}>
          {Object.entries(ROLE_LABELS).map(([role, label]) => <Select.Option key={role} value={role}>{label}</Select.Option>)}
        </Select>
        <Select allowClear placeholder="Tất cả trạng thái" value={statusFilter} onChange={setStatusFilter}>
          <Select.Option value={true}>Đang hoạt động</Select.Option><Select.Option value={false}>Đã khóa</Select.Option>
        </Select>
      </div>}>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredUsers}
        loading={loading}
        scroll={{ x: 1180 }}
        pagination={{
          current: pageData.page + 1,
          pageSize: pageData.size,
          total: pageData.totalElements,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={(pagination) => load(pagination.current - 1, pagination.pageSize)}
      />
      </ManagementPanel>
      <Modal
        className="upgrade-modal" width={610}
        title={editing ? "Cập nhật người dùng" : "Thêm người dùng"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        okButtonProps={{ disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
        closable={!saving}
        maskClosable={!saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false} disabled={saving}>
          <div className="form-section-heading">Thông tin cá nhân</div>
          <div className="form-grid">
          <Form.Item label="Tên đăng nhập" name="username" rules={[{ required: true, message: "Vui lòng nhập tên đăng nhập" }]}>
            <Input disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item label="Họ tên" name="displayName" rules={[{ required: true, message: "Vui lòng nhập họ tên" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Số điện thoại" name="phoneNumber" rules={[{ max: 30 }]}><Input type="tel" maxLength={30} /></Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true }, { type: "email", message: "Email không hợp lệ" }]}>
            <Input />
          </Form.Item>
          </div>
          <div className="form-section-heading">Truy cập hệ thống</div>
          <div className="form-grid">
          {!editing && <Form.Item label="Mật khẩu" name="password" rules={[{ required: true, min: 8, message: "Mật khẩu phải có ít nhất 8 ký tự" }]}>
            <Input.Password />
          </Form.Item>}
          <Form.Item label="Vai trò" name="role" rules={[{ required: true, message: "Vui lòng chọn vai trò" }]}>
            <Select>
              {roles.map((role) => <Select.Option key={role} value={role}>{ROLE_LABELS[role]}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="Hoạt động" name="status" valuePropName="checked">
            <Switch />
          </Form.Item>
          </div>
        </Form>
      </Modal>
      <Modal
        title={assignmentUser ? `Phân công tài khoản · ${assignmentUser.displayName}` : "Phân công tài khoản"}
        open={Boolean(assignmentUser)}
        onCancel={closeAssignments}
        onOk={saveAssignments}
        okText="Lưu phân công"
        cancelText="Hủy"
        confirmLoading={assignmentSaving || assignmentLoading}
        okButtonProps={{ disabled: assignmentSaving || assignmentLoading }}
        cancelButtonProps={{ disabled: assignmentSaving || assignmentLoading }}
        closable={!assignmentSaving && !assignmentLoading}
        maskClosable={!assignmentSaving && !assignmentLoading}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          Người dùng chỉ xem và thao tác trên những tài khoản được chọn.
        </Typography.Paragraph>
        <Select
          aria-label="Tài khoản được phân công"
          mode="multiple"
          loading={assignmentLoading}
          disabled={assignmentLoading || assignmentSaving}
          value={assignmentIds}
          onChange={setAssignmentIds}
          optionFilterProp="label"
          placeholder="Chọn tài khoản ngân hàng"
          style={{ width: "100%" }}
          options={assignmentOptions.map((account) => ({
            value: account.id,
            label: `${account.internalCode} · ${account.bankName} · ${account.accountNumber || account.accountNumberMasked || "—"}`,
          }))}
        />
      </Modal>
      <Modal
        className="permission-modal" width={930} title={null} open={Boolean(permissionUser)}
        onCancel={() => !permissionSaving && setPermissionUser(null)} footer={null}
        closable={!permissionSaving} maskClosable={!permissionSaving} destroyOnClose
      >
        <div className="permission-modal-heading">
          <Typography.Title level={3}>Phân quyền thao tác</Typography.Title>
          <Typography.Text type="secondary">Thiết lập quyền truy cập theo từng chức năng</Typography.Text>
        </div>
        {permissionUser && <div className="permission-user-card">
          <Avatar size={60}>{(permissionUser.displayName || permissionUser.username).charAt(0).toUpperCase()}</Avatar>
          <div><span>Họ và tên</span><strong>{permissionUser.displayName}</strong><span>Tên đăng nhập</span><strong>@{permissionUser.username}</strong></div>
          <div><span>Vai trò</span><strong>{ROLE_LABELS[permissionUser.role]}</strong><span>Trạng thái</span>
            <Tag color={permissionUser.status ? "green" : "default"}>{permissionUser.status ? "Đang hoạt động" : "Đã khóa"}</Tag></div>
        </div>}
        <div className="permission-list-heading">
          <Typography.Title level={4}>Danh sách quyền</Typography.Title>
          <Typography.Text type="secondary">{permissionsCustomized
            ? "Người dùng đang có cấu hình quyền riêng."
            : "Đang áp dụng quyền mặc định theo vai trò; lưu để tạo quyền riêng cho người dùng."}</Typography.Text>
        </div>
        <Table rowKey="feature" loading={permissionLoading} pagination={false} size="middle" bordered
          scroll={{ x: 760 }} dataSource={permissionRows}
          columns={[
            { title: "Chức năng", dataIndex: "label", width: 250, render: (value) => <Typography.Text strong>{value}</Typography.Text> },
            ...[["canView", "Xem thông tin"], ["canEdit", "Chỉnh sửa dữ liệu"], ["canDelete", "Xóa dữ liệu"]].map(([field, label]) => ({
              title: <Space size={8}><Checkbox aria-label={`Chọn tất cả ${label}`} checked={permissionRows.length > 0
                && permissionRows.filter((row) => field === "canView" || (field === "canEdit" ? row.supportsEdit : row.supportsDelete)).every((row) => row[field])}
                onChange={(event) => togglePermissionColumn(field, event.target.checked)} />{label}</Space>,
              dataIndex: field, align: "center", width: 170,
              render: (checked, row) => field === "canView" || (field === "canEdit" ? row.supportsEdit : row.supportsDelete)
                ? <Checkbox aria-label={`${label} · ${row.label}`} checked={checked}
                  onChange={(event) => updatePermission(row.feature, field, event.target.checked)} />
                : <Tooltip title="Chức năng chưa có thao tác tương ứng"><Typography.Text type="secondary">—</Typography.Text></Tooltip>,
            })),
          ]}
        />
        <div className="permission-modal-footer">
          <Button onClick={() => setPermissionUser(null)} disabled={permissionSaving}>Hủy</Button>
          <Button type="primary" onClick={savePermissions} loading={permissionSaving} disabled={permissionLoading}>Lưu phân quyền</Button>
        </div>
      </Modal>
      <Modal title={passwordUser ? `Đặt lại mật khẩu · ${passwordUser.displayName}` : "Đặt lại mật khẩu"}
        open={Boolean(passwordUser)} onCancel={() => !passwordSaving && setPasswordUser(null)}
        onOk={() => passwordForm.submit()} okText="Đặt lại mật khẩu" cancelText="Hủy"
        confirmLoading={passwordSaving} closable={!passwordSaving} maskClosable={!passwordSaving} destroyOnClose>
        <Form form={passwordForm} layout="vertical" onFinish={savePassword}>
          <Form.Item label="Mật khẩu mới" name="newPassword" rules={[{ required: true, min: 8, message: "Mật khẩu phải có ít nhất 8 ký tự" }]}><Input.Password /></Form.Item>
          <Form.Item label="Xác nhận mật khẩu" name="confirmPassword" dependencies={["newPassword"]} rules={[
            { required: true, message: "Vui lòng xác nhận mật khẩu" },
            ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue("newPassword") === value
              ? Promise.resolve() : Promise.reject(new Error("Xác nhận mật khẩu không khớp")); } }),
          ]}><Input.Password /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
